import { Midi } from "@tonejs/midi";
import { useCallback, useEffect, useRef, useState } from "react";

interface MidiNote {
  time: number;
  duration: number;
  midi: number;
  velocity: number;
}

interface MidiPlayerProps {
  src: string;
  durationMs: number;
  listenedMs?: number;
  minimumMs?: number;
  disabled?: boolean;
  onListened?: (milliseconds: number) => void;
  onEnded?: () => void;
  compact?: boolean;
}

interface SharedEngine {
  context: AudioContext;
  output: DynamicsCompressorNode;
  pianoWave: PeriodicWave;
}

let sharedEngine: SharedEngine | null = null;
let activePause: (() => void) | null = null;
const PLAYBACK_RATE = 1.25;

function engine(): SharedEngine {
  if (sharedEngine) return sharedEngine;
  const context = new AudioContext();
  const output = context.createDynamicsCompressor();
  output.threshold.value = -18;
  output.knee.value = 18;
  output.ratio.value = 5;
  output.attack.value = 0.003;
  output.release.value = 0.22;
  output.connect(context.destination);
  const pianoWave = context.createPeriodicWave(
    new Float32Array([0, 0, 0, 0, 0, 0, 0]),
    new Float32Array([0, 1, 0.58, 0.31, 0.16, 0.08, 0.035]),
    { disableNormalization: false },
  );
  sharedEngine = { context, output, pianoWave };
  return sharedEngine;
}

function formatTime(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

function frequency(pitch: number): number {
  return 440 * 2 ** ((pitch - 69) / 12);
}

function PianoRoll({ notes, duration, playhead }: { notes: MidiNote[]; duration: number; playhead: number }) {
  const width = 760;
  const height = 126;
  const low = Math.max(0, Math.min(...notes.map((note) => note.midi), 60) - 2);
  const high = Math.min(127, Math.max(...notes.map((note) => note.midi), 72) + 2);
  const span = Math.max(12, high - low + 1);
  const beatCount = Math.max(1, Math.round(duration * 2));
  return (
    <svg className="piano-roll" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Piano roll visualization">
      <rect width={width} height={height} className="roll-background" />
      {Array.from({ length: beatCount + 1 }, (_, index) => (
        <line key={index} x1={(index / beatCount) * width} x2={(index / beatCount) * width} y1="0" y2={height} className={index % 4 === 0 ? "roll-bar" : "roll-beat"} />
      ))}
      {notes.map((note, index) => {
        const x = duration > 0 ? (note.time / duration) * width : 0;
        const noteWidth = Math.max(2, duration > 0 ? (note.duration / duration) * width : 2);
        const y = height - ((note.midi - low + 1) / span) * height;
        return <rect key={`${note.time}-${note.midi}-${index}`} x={x} y={y} width={noteWidth} height={Math.max(2.4, height / span * 0.72)} rx="1.5" className="roll-note" />;
      })}
      <line x1={(Math.min(playhead, duration) / Math.max(duration, 0.001)) * width} x2={(Math.min(playhead, duration) / Math.max(duration, 0.001)) * width} y1="0" y2={height} className="roll-playhead" />
    </svg>
  );
}

export function MidiPlayer({
  src,
  durationMs,
  listenedMs = 0,
  minimumMs = 0,
  disabled = false,
  onListened,
  onEnded,
  compact = false,
}: MidiPlayerProps) {
  const [notes, setNotes] = useState<MidiNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [playing, setPlaying] = useState(false);
  const playingRef = useRef(false);
  const [currentTime, setCurrentTime] = useState(0);
  const nodesRef = useRef<AudioScheduledSourceNode[]>([]);
  const frameRef = useRef(0);
  const startedAtRef = useRef(0);
  const startPositionRef = useRef(0);
  const accumulatedRef = useRef(listenedMs);
  const lastContextTimeRef = useRef(0);
  const duration = durationMs / 1000 / PLAYBACK_RATE;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    fetch(src, { cache: "force-cache" })
      .then((response) => {
        if (!response.ok) throw new Error(`MIDI HTTP ${response.status}`);
        return response.arrayBuffer();
      })
      .then((buffer) => {
        const midi = new Midi(buffer);
        const parsed = midi.tracks.flatMap((track) => track.notes).map((note) => ({
          time: note.time / PLAYBACK_RATE,
          duration: note.duration / PLAYBACK_RATE,
          midi: note.midi,
          velocity: note.velocity,
        })).sort((left, right) => left.time - right.time || left.midi - right.midi);
        if (!cancelled) setNotes(parsed);
      })
      .catch((reason: unknown) => {
        if (!cancelled) setError(reason instanceof Error ? reason.message : "Could not load MIDI");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [src]);

  useEffect(() => {
    accumulatedRef.current = Math.max(accumulatedRef.current, listenedMs);
  }, [listenedMs]);

  const clearNodes = useCallback(() => {
    for (const node of nodesRef.current) {
      try { node.stop(); } catch { /* already stopped */ }
      node.disconnect();
    }
    nodesRef.current = [];
  }, []);

  const pause = useCallback(() => {
    if (!playingRef.current) return;
    const audio = engine();
    const elapsed = Math.max(0, audio.context.currentTime - startedAtRef.current);
    const position = Math.min(duration, startPositionRef.current + elapsed);
    const heard = Math.max(0, audio.context.currentTime - lastContextTimeRef.current) * 1000;
    accumulatedRef.current += heard;
    onListened?.(Math.floor(accumulatedRef.current));
    setCurrentTime(position);
    playingRef.current = false;
    setPlaying(false);
    clearNodes();
    cancelAnimationFrame(frameRef.current);
    activePause = null;
  }, [clearNodes, duration, onListened]);

  const animate = useCallback(() => {
    const audio = engine();
    const now = audio.context.currentTime;
    const position = Math.min(duration, startPositionRef.current + now - startedAtRef.current);
    const heard = Math.max(0, Math.min(0.1, now - lastContextTimeRef.current));
    lastContextTimeRef.current = now;
    accumulatedRef.current += heard * 1000;
    onListened?.(Math.floor(accumulatedRef.current));
    setCurrentTime(position);
    if (position >= duration) {
      playingRef.current = false;
      setPlaying(false);
      clearNodes();
      activePause = null;
      onEnded?.();
      return;
    }
    frameRef.current = requestAnimationFrame(animate);
  }, [clearNodes, duration, onEnded, onListened]);

  const play = async () => {
    if (disabled || loading || error || notes.length === 0) return;
    if (playingRef.current) {
      pause();
      return;
    }
    activePause?.();
    const audio = engine();
    await audio.context.resume();
    const position = currentTime >= duration ? 0 : currentTime;
    const begins = audio.context.currentTime + 0.035;
    clearNodes();
    for (const note of notes) {
      const noteEnd = note.time + note.duration;
      if (noteEnd <= position) continue;
      const effectiveStart = Math.max(note.time, position);
      const startsAt = begins + effectiveStart - position;
      const sounding = Math.max(0.035, noteEnd - effectiveStart);
      const oscillator = audio.context.createOscillator();
      const gain = audio.context.createGain();
      oscillator.setPeriodicWave(audio.pianoWave);
      oscillator.frequency.value = frequency(note.midi);
      const peak = 0.032 + Math.min(0.045, note.velocity * 0.045);
      gain.gain.setValueAtTime(0.0001, startsAt);
      gain.gain.exponentialRampToValueAtTime(peak, startsAt + 0.004);
      gain.gain.exponentialRampToValueAtTime(Math.max(0.004, peak * 0.28), Math.min(startsAt + sounding, startsAt + 0.075));
      gain.gain.exponentialRampToValueAtTime(0.0001, startsAt + sounding + Math.min(0.38, sounding * 0.42));
      oscillator.connect(gain);
      gain.connect(audio.output);
      oscillator.start(startsAt);
      oscillator.stop(startsAt + sounding + Math.min(0.4, sounding * 0.44));
      nodesRef.current.push(oscillator);
    }
    startPositionRef.current = position;
    startedAtRef.current = begins;
    lastContextTimeRef.current = begins;
    setCurrentTime(position);
    playingRef.current = true;
    setPlaying(true);
    activePause = pause;
    frameRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => () => {
    playingRef.current = false;
    clearNodes();
    cancelAnimationFrame(frameRef.current);
  }, [clearNodes]);

  const reached = minimumMs === 0 || listenedMs >= minimumMs;
  const progress = duration > 0 ? Math.min(100, currentTime / duration * 100) : 0;
  return (
    <div className={`audio-player midi-player ${compact ? "compact" : ""} ${disabled ? "disabled" : ""}`}>
      <PianoRoll notes={notes} duration={duration} playhead={currentTime} />
      <div className="midi-controls">
        <button className="play-button" type="button" onClick={() => void play()} disabled={disabled || loading || Boolean(error)} aria-label={playing ? "Pause" : "Play"}>
          {playing ? "Ⅱ" : "▶"}
        </button>
        <div className="audio-track" aria-hidden="true"><div className="audio-progress" style={{ width: `${progress}%` }} /></div>
        <span className="audio-time">{formatTime(currentTime)} / {formatTime(duration)}</span>
        <span className="playback-rate">{PLAYBACK_RATE.toFixed(2)}×</span>
        {minimumMs > 0 && <span className={`listen-status ${reached ? "reached" : ""}`}>{reached ? "Listening complete" : `${Math.min(Math.ceil(minimumMs / 1000), Math.floor(listenedMs / 1000))}/${Math.ceil(minimumMs / 1000)}s`}</span>}
      </div>
      {loading && <span className="midi-message">Loading MIDI…</span>}
      {error && <span className="midi-message error">{error}</span>}
    </div>
  );
}
